/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from 'react';
import { ArrowLeft, Search, Rocket, Brain, Shield, Wallet, ChevronDown, ChevronUp, MessageCircle, Mail, ExternalLink, Zap } from 'lucide-react';

interface HelpCenterProps {
    onBack: () => void;
}

interface Article {
    id: string;
    category: 'START' | 'LAUNCH' | 'TRADE' | 'SAFETY';
    question: string;
    answer: React.ReactNode;
}

const KNOWLEDGE_BASE: Article[] = [
    // Getting Started
    {
        id: '1',
        category: 'START',
        question: 'How do I connect my wallet?',
        answer: 'Click the "Connect Wallet" button in the top right corner. We support Phantom and Solflare for Solana, and Sui Wallet or Suiet for the Sui Network. You can also use our Embedded Wallet feature to login with just an email address.'
    },
    {
        id: '2',
        category: 'START',
        question: 'What networks are supported?',
        answer: 'HotLaunchpad currently supports Solana (Mainnet & Devnet) and Sui (Mainnet & Devnet). You can toggle between networks using the dropdown selector in the navigation bar.'
    },
    // Launching
    {
        id: '3',
        category: 'LAUNCH',
        question: 'How much does it cost to launch a token?',
        answer: 'On Solana, the base service fee is 0.1 SOL plus network rent (~0.02 SOL). If you use advanced Token-2022 extensions like transfer fees, additional costs apply. On Sui, the package publication fee is typically around 5-10 SUI depending on network congestion.'
    },
    {
        id: '4',
        category: 'LAUNCH',
        question: 'What is a Bonding Curve launch?',
        answer: 'A bonding curve launch allows you to sell tokens instantly without providing initial liquidity. The price increases mathematically as people buy. Once the market cap target is hit (~85 SOL), liquidity is automatically migrated to Raydium/Cetus and locked.'
    },
    {
        id: '5',
        category: 'LAUNCH',
        question: 'Can I update my token metadata after launch?',
        answer: 'If you selected "Immutable" during launch, metadata cannot be changed. If you kept mutability enabled, you can use external tools like Metaplex to update metadata, though HotLaunchpad currently only supports immutable launches for security trust.'
    },
    // Trading
    {
        id: '6',
        category: 'TRADE',
        question: 'How does the AI Swarm Engine work?',
        answer: 'Our Neural Engine aggregates data from hundreds of simulated analysis nodes. It monitors on-chain volume, smart money wallet movements, and social sentiment to generate a "Confidence Score" (0-100%). Signals over 80% indicate high conviction.'
    },
    {
        id: '7',
        category: 'TRADE',
        question: 'Why did my transaction fail?',
        answer: 'Common reasons include: Insufficient Slippage (try increasing it in settings), Insufficient Gas (you need SOL/SUI for fees), or network congestion. If trading on a bonding curve, the curve might have just graduated, requiring you to trade on the DEX instead.'
    },
    // Safety
    {
        id: '8',
        category: 'SAFETY',
        question: 'Is the platform audited?',
        answer: 'Our core smart contracts utilize standard, battle-tested libraries (OpenBook, Raydium CPMM, Metaplex). However, tokens launched by users are user-generated content. Always DYOR (Do Your Own Research) before trading user coins.'
    },
    {
        id: '9',
        category: 'SAFETY',
        question: 'What is "Revoke Mint Authority"?',
        answer: 'Revoking mint authority means no new tokens can ever be created. This caps the supply permanently, preventing inflation or "printing" attacks. It is a key safety indicator for investors.'
    }
];

const CATEGORIES = [
    { id: 'START', label: 'Getting Started', icon: Wallet, color: 'bg-blue-100 text-blue-600' },
    { id: 'LAUNCH', label: 'Launching Tokens', icon: Rocket, color: 'bg-purple-100 text-purple-600' },
    { id: 'TRADE', label: 'Trading & AI', icon: Brain, color: 'bg-amber-100 text-amber-600' },
    { id: 'SAFETY', label: 'Safety & Security', icon: Shield, color: 'bg-green-100 text-green-600' },
];

export const HelpCenter: React.FC<HelpCenterProps> = ({ onBack }) => {
    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState<string | 'ALL'>('ALL');
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const filteredArticles = useMemo(() => {
        return KNOWLEDGE_BASE.filter(article => {
            const matchesSearch = article.question.toLowerCase().includes(search.toLowerCase()) || 
                                  (typeof article.answer === 'string' && article.answer.toLowerCase().includes(search.toLowerCase()));
            const matchesCategory = activeCategory === 'ALL' || article.category === activeCategory;
            return matchesSearch && matchesCategory;
        });
    }, [search, activeCategory]);

    const toggleArticle = (id: string) => {
        setExpandedId(prev => prev === id ? null : id);
    };

    return (
        <div className="min-h-screen bg-[#F9F8F4] flex flex-col relative animate-fade-in">
            <main className="flex-1 pb-24">
                
                {/* Header / Hero */}
                <div className="bg-stone-900 pt-12 pb-24 px-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-nobel-gold/10 rounded-full blur-[100px] pointer-events-none"></div>
                    <div className="container mx-auto max-w-4xl relative z-10">
                        <button onClick={onBack} className="flex items-center gap-2 text-stone-400 hover:text-white transition-colors mb-8">
                            <ArrowLeft size={18} /> Back to App
                        </button>
                        
                        <div className="text-center">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-stone-800 border border-stone-700 rounded-full mb-6">
                                <Zap size={12} className="text-nobel-gold" />
                                <span className="text-[10px] font-bold text-stone-300 uppercase tracking-widest">Support Center</span>
                            </div>
                            <h1 className="font-serif text-4xl md:text-5xl text-white mb-8">How can we help you?</h1>
                            
                            <div className="relative max-w-2xl mx-auto">
                                <input 
                                    type="text" 
                                    placeholder="Search for answers (e.g. 'bonding curve', 'fees')"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="w-full pl-12 pr-4 py-4 bg-white rounded-xl text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-4 focus:ring-nobel-gold/30 shadow-xl transition-all"
                                />
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={20} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="container mx-auto px-6 max-w-4xl -mt-12 relative z-20">
                    
                    {/* Category Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
                        {CATEGORIES.map(cat => (
                            <button
                                key={cat.id}
                                onClick={() => setActiveCategory(cat.id === activeCategory ? 'ALL' : cat.id)}
                                className={`p-4 rounded-xl border transition-all duration-300 flex flex-col items-center gap-3 shadow-sm hover:-translate-y-1 ${
                                    activeCategory === cat.id 
                                    ? 'bg-white border-nobel-gold ring-2 ring-nobel-gold/20' 
                                    : 'bg-white border-stone-200 hover:border-stone-300'
                                }`}
                            >
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cat.color}`}>
                                    <cat.icon size={20} />
                                </div>
                                <span className="font-bold text-sm text-stone-700">{cat.label}</span>
                            </button>
                        ))}
                    </div>

                    {/* FAQ List */}
                    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden mb-12">
                        <div className="p-6 border-b border-stone-100 flex justify-between items-center">
                            <h3 className="font-serif text-2xl text-stone-900">
                                {search ? 'Search Results' : activeCategory === 'ALL' ? 'Popular Articles' : CATEGORIES.find(c => c.id === activeCategory)?.label}
                            </h3>
                            <span className="text-xs font-bold text-stone-400 uppercase tracking-wide">
                                {filteredArticles.length} Articles
                            </span>
                        </div>
                        
                        {filteredArticles.length > 0 ? (
                            <div className="divide-y divide-stone-100">
                                {filteredArticles.map(article => (
                                    <div key={article.id} className="group">
                                        <button 
                                            onClick={() => toggleArticle(article.id)}
                                            className="w-full text-left p-6 flex items-start justify-between hover:bg-stone-50 transition-colors"
                                        >
                                            <span className="font-bold text-stone-800 group-hover:text-nobel-gold transition-colors">{article.question}</span>
                                            {expandedId === article.id ? <ChevronUp size={20} className="text-stone-400 shrink-0" /> : <ChevronDown size={20} className="text-stone-400 shrink-0" />}
                                        </button>
                                        {expandedId === article.id && (
                                            <div className="px-6 pb-6 text-stone-600 text-sm leading-relaxed animate-fade-in">
                                                {article.answer}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-12 text-center text-stone-500">
                                <Search size={48} className="mx-auto text-stone-200 mb-4" />
                                <p>No articles found for "{search}".</p>
                                <button onClick={() => { setSearch(''); setActiveCategory('ALL'); }} className="mt-4 text-nobel-gold font-bold hover:underline">Clear Filters</button>
                            </div>
                        )}
                    </div>

                    {/* Contact Footer */}
                    <div className="bg-stone-900 rounded-2xl p-8 md:p-10 text-center relative overflow-hidden text-white">
                        <div className="relative z-10">
                            <h3 className="font-serif text-2xl font-bold mb-4">Still need help?</h3>
                            <p className="text-stone-400 mb-8 max-w-lg mx-auto">
                                Our support team is available 24/7. Join our community channels for the fastest response from moderators and the team.
                            </p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <a href="mailto:support@hotnetwork.app" className="flex items-center gap-2 px-6 py-3 bg-white text-stone-900 rounded-lg font-bold text-sm hover:bg-stone-100 transition-colors">
                                    <Mail size={16} /> Email Support
                                </a>
                                <a href="https://t.me/HotNetwork" target="_blank" className="flex items-center gap-2 px-6 py-3 bg-[#0088cc] text-white rounded-lg font-bold text-sm hover:brightness-110 transition-colors">
                                    <MessageCircle size={16} /> Telegram Community
                                </a>
                                <a href="#" className="flex items-center gap-2 px-6 py-3 bg-[#5865F2] text-white rounded-lg font-bold text-sm hover:brightness-110 transition-colors">
                                    <ExternalLink size={16} /> Discord Server
                                </a>
                            </div>
                        </div>
                    </div>

                </div>
            </main>
        </div>
    );
};